import React, {useEffect, useState, useRef} from 'react';
import {Button, Checkbox, Dropdown, Input, MenuProps, message, Modal, Space, Typography, Tabs} from 'antd';
import {DownOutlined, MailOutlined, SafetyOutlined} from '@ant-design/icons';
import Config from "./Config";
import Cart from "./Cart";
import {showUsageInfo} from "./UsageInfo";
import TokenModal from "./TokenModal";
import eventBus from '../../utils/eventBus';
import {isElectron} from '../../utils/osUtils';
import { useTranslation } from '../../hooks/useTranslation';
import { isEnglishSite } from '../../config/site';
import { useUserInfo } from '../../contexts/UserInfoContext';

const {Paragraph, Text} = Typography;
const { TabPane } = Tabs;

const USER_SERVICE_AGREEMENT_URL = 'https://s.imgbed.cn/autoupload/fdd/20260710/folder-mrf2uvho-d2wye8ym/tuchuang-xiaozhen-legal/user-service-agreement.html';
const PRIVACY_POLICY_URL = 'https://s.imgbed.cn/autoupload/fdd/20260710/folder-mrf2uvho-d2wye8ym/tuchuang-xiaozhen-legal/privacy-policy.html';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { clear, isActiveVip, user } = useUserInfo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [sendingCode, setSendingCode] = useState(false)
  const [loginLoading, setLoginLoading] = useState(false)
  const [showConfig, setShowConfig] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [hasInviteCode, setHasInviteCode] = useState(false);
  const [isElectronEnv, setIsElectronEnv] = useState(false);
  const [isUsDomain, setIsUsDomain] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(true);

  // 验证码输入框的引用
  const verifyCodeInputRef = useRef<any>(null);

  // 从 URL 参数中获取邀请码，兼容历史 hash 邀请链接
  const getInviteCodeFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const inviteFromSearch = searchParams.get('invite');
    if (inviteFromSearch) {
      return decodeURIComponent(inviteFromSearch);
    }

    const hash = window.location.hash;
    if (hash) {
      const hashMatch = hash.match(/[#&?]invite=([^&]+)/);
      if (hashMatch && hashMatch[1]) {
        return decodeURIComponent(hashMatch[1]);
      }
    }
    return null;
  };


  useEffect(() => {
    // 检测 URL 中是否有邀请码
    const inviteCode = getInviteCodeFromUrl();
    setHasInviteCode(!!inviteCode);

    // 检测是否运行在 Electron 环境中
    setIsElectronEnv(isElectron());

    // 检测是否为仅允许验证码登录的域名（us.imgbed.cn 和 mufy.imgbed.cn）
    const hostname = window.location.hostname;
    setIsUsDomain(
      hostname === 'us.imgbed.cn' || 
      hostname === 'www.us.imgbed.cn' ||
      hostname === 'mufy.imgbed.cn' ||
      hostname === 'www.mufy.imgbed.cn'
    );

    // 使用已导入的 EventBus 模块
    const openLoginModalHandler = () => {
      setAgreementChecked(true);
      setIsModalOpen(true);
    };

    eventBus.on('openLoginModal', openLoginModalHandler);

    // 清理事件监听器
    return () => {
      eventBus.off('openLoginModal', openLoginModalHandler);
    };
  }, [])

  const showModal = () => {
    setAgreementChecked(true);
    setIsModalOpen(true);
  };

  const checkAgreement = () => {
    if (!agreementChecked) {
      message.error(t('login.agreementRequired'));
      return false;
    }
    return true;
  };

  const handleOk = () => {
    if (email.trim() === '') {
      message.error(t('login.emailPlaceholder'))
      return
    }

    if (!email.includes('@')) {
      if (email.includes(' ')) {
        // 这里用户可能输入的是激活卡号密码
        message.error(t('login.emailErrorWithActivation'))
      } else {
        message.error(t('login.emailError'))
      }

      return;
    }

    if (!checkAgreement()) {
      return;
    }

    // 发送登录链接
    fetch('/login/email', {
      method: 'POST',
      body: JSON.stringify({
        email: email.trim(),
        host: window.location.host,
      }),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((e) => {
      console.info(e)
      if (e.isSuccess) {
        message.info(e.message || t('login.checkEmail'))
        setIsModalOpen(false)
      } else {
        message.error(e.message || t('login.sendFailed'))
      }
    })
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEmail('');
    setVerifyCode('');
    setCountdown(0);
    setAgreementChecked(true);
  };

  // 发送验证码
  const handleSendCode = async () => {
    if (email.trim() === '') {
      message.error(t('login.emailPlaceholder'));
      return;
    }

    if (!email.includes('@')) {
      message.error(t('login.emailError'));
      return;
    }

    if (!checkAgreement()) {
      return;
    }

    setSendingCode(true);
    try {
      const response = await fetch('/login/send-code', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          host: window.location.host,
        }),
        headers: {
          'Content-Type': 'application/json;charset=utf8'
        }
      });
      const result = await response.json();

      if (result.isSuccess) {
        message.success(result.message || t('login.codeSent'));
        // 开始倒计时
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        message.error(result.message || t('login.sendFailed'));
      }
    } catch (error) {
      message.error(t('login.networkError'));
    }
    setSendingCode(false);
  };

  // 验证码登录
  const handleCodeLogin = async () => {
    if (email.trim() === '') {
      message.error(t('login.emailPlaceholder'));
      return;
    }

    if (!email.includes('@')) {
      message.error(t('login.emailError'));
      return;
    }

    if (verifyCode.trim() === '') {
      message.error(t('login.verifyCodeError'));
      return;
    }

    if (!/^\d{6}$/.test(verifyCode)) {
      message.error(t('login.verifyCodeFormatError'));
      return;
    }

    if (!checkAgreement()) {
      return;
    }

    setLoginLoading(true);
    try {
      // 从 URL 获取邀请码（如果有）
      const inviteCode = getInviteCodeFromUrl();
      
      const response = await fetch('/login/verify-code', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          code: verifyCode.trim(),
          inviteCode: inviteCode || undefined, // 如果有邀请码则传递
        }),
        headers: {
          'Content-Type': 'application/json;charset=utf8'
        }
      });
      const result = await response.json();

      if (result.isSuccess) {
        message.success(t('login.loginSuccess'));
        setIsModalOpen(false);
        // 刷新页面以更新用户信息，并清除 URL 中的邀请码参数
        const cleanUrl = window.location.origin + window.location.pathname;
        window.location.href = cleanUrl;
      } else {
        message.error(result.message || t('login.loginFailed'));
      }
    } catch (error) {
      message.error(t('login.networkError'));
    }
    setLoginLoading(false);
  };

  const handleViewToken = () => {
    setShowTokenModal(true);
  };

  const renderAgreementCheckbox = () => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, flexShrink: 0 }}>
        <Checkbox
          aria-label={t('login.agreementCheckboxAria')}
          checked={agreementChecked}
          onChange={(e) => setAgreementChecked(e.target.checked)}
          style={{ lineHeight: 1 }}
        />
      </span>
      <Text type="secondary" style={{ fontSize: 12, lineHeight: '20px' }}>
        {t('login.agreementPrefix')}
        <a
          href={USER_SERVICE_AGREEMENT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('login.userServiceAgreement')}
        </a>
        {t('login.agreementAnd')}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('login.privacyPolicy')}
        </a>
      </Text>
    </div>
  );

  const items: MenuProps['items'] = [
    {
      key: '0',
      label: (
        t('login.settings')
      ),
      onClick: (e) => {
        setShowConfig(true);
      }
    },
    {
      key: '1',
      label: (
        t('login.buyVip')
      ),
      onClick: (e) => {
        setShowCart(true);
      }
    },
    // 英文站隐藏「查看使用情况」入口（含 Online Status）
    ...(!isEnglishSite() ? [{
      key: '2',
      label: t('login.viewUsage'),
      onClick: () => showUsageInfo(),
    }] : []),
    {
      key: '3',
      label: (
        t('login.viewToken')
      ),
      onClick: (e) => {
        handleViewToken();
      }
    },
    // {
    //   key: '4',
    //   label: (
    //     t('login.invite')
    //   ),
    //   onClick: (e) => {
    //     navigate('/invite');
    //   }
    // },
    {
      key: '999',
      label: (
        t('login.logout')
      ),
      onClick: (e) => {
        fetch('/login/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=utf8'
          }
        }).then(res => res.json()).then((e) => {
          clear();
          message.success(e.message || t('login.logoutSuccess'))
        })
      }
    },
  ];

  return (
    <>
      {
        user?.email && (
          <>
            {
              !isActiveVip && (
                <Button color="danger" type="link" onClick={() => setShowCart(true)}>
                  {t('login.activateVip')}
                </Button>
              )
            }
            <Dropdown menu={{items}}>
              <button
                onClick={(e) => e.preventDefault()}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  overflowX: "hidden"
                }}>
                <span style={{
                  "height": "20px",
                  "fontFamily": "PingFangSC",
                  "fontWeight": 400,
                  "fontSize": "12px",
                  "color": "rgb(76, 83, 98)",
                  "lineHeight": "20px",
                  "overflow": "hidden",
                  "textOverflow": "ellipsis",
                }}>{user.email}</span>
                <DownOutlined style={{ fontSize: '10px', color: 'rgb(76, 83, 98)' }} />
              </button>
            </Dropdown>
          </>
        )
      }

      {
        !user?.email && (
          <>
            {!isEnglishSite() && (
              <Button
                type="text"
                size="small"
                onClick={() => setShowConfig(true)}
                style={{
                  "height": "20px",
                  "fontFamily": "PingFangSC",
                  "fontWeight": 400,
                  "fontSize": "12px",
                  "color": "#4C5362",
                  "lineHeight": "20px",
                  padding: '0 8px'
                }}
              >
                {t('config.language')}
              </Button>
            )}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                showModal();
              }}
              style={{
                "height": "20px",
                "fontFamily": "PingFangSC",
                textDecoration: 'none',
                cursor: 'pointer',
                "fontWeight": 400,
                "fontSize": "12px",
                "color": "#4C5362",
                "lineHeight": "20px"
              }}
            >
              {t('login.login')}
            </a>
          </>
        )
      }
      <Modal
        title={t('login.title')}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={450}
      >
        <Tabs defaultActiveKey="code">
          {/* 邮件链接登录 - 当有邀请码、Electron 环境时禁用，us.imgbed.cn 和 mufy.imgbed.cn 域名时完全隐藏 */}
          {!isUsDomain && (
            <TabPane 
              tab={t('login.emailLogin')} 
              key="link"
              disabled={hasInviteCode || isElectronEnv}
            >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Input
                prefix={<MailOutlined />}
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                type="email"
                size="large"
              />

              {renderAgreementCheckbox()}

              <Button
                type="primary"
                size="large"
                block
                onClick={handleOk}
                disabled={!agreementChecked}
              >
                {t('login.sendLoginLink')}
              </Button>

              <Paragraph style={{ marginBottom: 0 }}>
                <Text type="danger" strong underline>
                  {t('login.emailPlaceholder')}
                </Text>
                {t('login.systemWillSend')}
                <Text strong>
                  {t('login.loginLink')}
                </Text>
                {t('login.toYourEmail')}
                <Text underline strong type="danger">{t('login.activateVipHint')}</Text>，{t('login.activateVipAfterLogin')}。
              </Paragraph>
            </Space>
          </TabPane>
          )}

          {/* 验证码登录 */}
          <TabPane tab={hasInviteCode ? t('login.codeLoginRecommended') : t('login.codeLogin')} key="code">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Input
                prefix={<MailOutlined />}
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                type="email"
                size="large"
                onKeyDown={(e) => {
                  // 检查是否正在输入中文（组合输入状态）
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    // 按 Enter 键时，将焦点移动到验证码输入框
                    e.preventDefault();
                    verifyCodeInputRef.current?.focus();
                  }
                }}
              />

              {renderAgreementCheckbox()}

              <Space.Compact style={{ width: '100%' }}>
                <Input
                  ref={verifyCodeInputRef}
                  prefix={<SafetyOutlined />}
                  placeholder={t('login.verifyCodePlaceholder')}
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value)}
                  maxLength={6}
                  size="large"
                  style={{ width: 'calc(100% - 120px)' }}
                  onKeyDown={(e) => {
                    // 检查是否正在输入中文（组合输入状态）
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      // 按 Enter 键时，触发登录
                      e.preventDefault();
                      handleCodeLogin();
                    }
                  }}
                />
                <Button
                  size="large"
                  onClick={handleSendCode}
                  disabled={countdown > 0 || sendingCode || !agreementChecked}
                  loading={sendingCode}
                  style={{ width: 120 }}
                >
                  {countdown > 0 ? t('login.retryAfter', { seconds: countdown }) : t('login.sendCode')}
                </Button>
              </Space.Compact>

              <Button
                type="primary"
                size="large"
                block
                onClick={handleCodeLogin}
                loading={loginLoading}
                disabled={!agreementChecked}
              >
                {t('login.loginButton')}
              </Button>

              <Paragraph style={{ marginBottom: 0, fontSize: 12, color: '#666' }}>
                <Text type="secondary">
                  {t('login.codeHint')}
                </Text>
              </Paragraph>
              {hasInviteCode && (
                <Paragraph style={{ marginBottom: 0, fontSize: 12, color: '#1890ff' }}>
                  <Text type="secondary" style={{ color: '#1890ff' }}>
                    {t('login.inviteCodeDetected')}
                  </Text>
                </Paragraph>
              )}
            </Space>
          </TabPane>
        </Tabs>
      </Modal>

      <Modal title={t('login.settingsCenter')} footer={null} open={showConfig} okText={t('common.close')} cancelText={t('common.close')}
             onCancel={() => setShowConfig(false)} onOk={() => {
        setShowConfig(false)
      }}>
        <Config/>
      </Modal>

      <Modal title={t('shortUrl.vipCenter')} footer={null} width={"80%"} open={showCart} okText={t('common.close')} cancelText={t('common.close')}
             onCancel={() => setShowCart(false)} onOk={() => {
        setShowCart(false)
      }}>
        <Cart/>
      </Modal>

      <TokenModal 
        open={showTokenModal} 
        onCancel={() => setShowTokenModal(false)} 
      />
    </>
  );
};

export default Login;
